import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { type MergeAgenciesRequest, mergeAgencies } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Agency } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { MAX_MERGE_AGENCIES } from './mergeFields';

type UseBulkMergeOptions = {
  /** Called after a successful merge so the caller can refetch the grid. */
  onSuccess?: () => void;
};

/** Success toast text; the app navigates to the new agency, so the toast carries no link. */
export function buildMergeSuccessMessage(mergedAgencyName: string, mergedCount: number): string {
  return `Merged ${mergedCount} ${mergedCount === 1 ? 'agency' : 'agencies'} into ${mergedAgencyName}.`;
}

const GENERIC_MERGE_ERROR = 'Failed to merge agencies. Please try again.';

/** The server's reason for a 400 (a selection or value it rejects), else the generic retry message. */
export function mergeErrorMessage(error: unknown): string {
  if (!isAxiosError(error) || error.response?.status !== 400) return GENERIC_MERGE_ERROR;
  const data: unknown = error.response.data;
  const reason = typeof data === 'object' && data !== null ? (data as { reason?: unknown }).reason : data;
  return typeof reason === 'string' && reason.trim() !== ''
    ? `Couldn't merge agencies: ${reason}`
    : GENERIC_MERGE_ERROR;
}

export function useBulkMerge({ onSuccess }: UseBulkMergeOptions = {}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedAgencies, setSelectedAgencies] = useState<Agency[]>([]);

  const mutation = useMutation({
    mutationFn: (params: MergeAgenciesRequest) => mergeAgencies(params),
    onSuccess: (data, variables) => {
      const mergedName = variables.values?.agencyName ?? selectedAgencies[0]?.agencyName ?? 'the merged agency';
      Notify.info({ message: buildMergeSuccessMessage(mergedName, variables.agencyIds.length) });
      setOpen(false);
      setSelectedAgencies([]);
      onSuccess?.();
      navigate({
        to: '/admin/civil-search/agencies/$agencyId/$tab',
        params: { agencyId: data.mergedAgencyId, tab: 'general' },
      });
    },
    onError: (error) => {
      Notify.error({ message: mergeErrorMessage(error) });
    },
  });

  /**
   * Open the dialog with 2 to 10 selected agencies, in selection order. The caller validates the
   * selection first; any other count is a programmer error, so it logs and bails.
   */
  const openWithAgencies = useCallback((agencies: Agency[]) => {
    if (agencies.length < 2 || agencies.length > MAX_MERGE_AGENCIES) {
      console.warn('useBulkMerge.openWithAgencies expected 2 to 10 agencies, got', agencies.length);
      return;
    }
    setSelectedAgencies(agencies);
    setOpen(true);
    capturePosthogEvent(POSTHOG_EVENTS.agencyMergeDialogOpened, {
      agency_ids: agencies.map((a) => a.id),
    });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setSelectedAgencies([]);
  }, []);

  const onSubmit = useCallback(
    (params: MergeAgenciesRequest) => {
      capturePosthogEvent(POSTHOG_EVENTS.agencyMergeSubmitted, {
        agency_ids: params.agencyIds,
        agency_count: params.agencyIds.length,
      });
      mutation.mutate(params);
    },
    [mutation.mutate],
  );

  return {
    open,
    selectedAgencies,
    openWithAgencies,
    close,
    onSubmit,
    isPending: mutation.isPending,
  };
}
